import { Alert, AlertTitle, Stack, Typography } from "@mui/material";

/**
 * The fields both mutation results share, plus the flavor-specific ids. Written
 * as one optional-superset type so a single component renders either result.
 */
export type IntegrationOrderOutcome = {
  readonly orderUuid?: string | null;
  readonly orderName?: string | null;
  readonly merged: boolean;
  readonly mergedIntoOrderUuid?: string | null;
  readonly mergedIntoOrderName?: string | null;
  readonly message?: string | null;
  readonly inboundApiRequestUuid?: string | null;
  readonly ediTransactionId?: string | null;
  readonly schemaWarning?: string | null;
};

type OrderOutcomeAlertProps = {
  /** The mutation's result, or null before the first submission. */
  readonly outcome: IntegrationOrderOutcome | null;
  /** The message of a failed submission. */
  readonly error: string | null;
};

/**
 * Renders what a submission did: the order it created, or the order it merged
 * into, or why it failed.
 *
 * A merge is shown as a warning rather than an error, because it is a valid and
 * often intended outcome here — the tool exists to exercise duplicate-order
 * merge behavior, so landing on an existing order is a result to read, not a
 * problem to fix.
 */
const OrderOutcomeAlert = ({ outcome, error }: OrderOutcomeAlertProps) => {
  if (error !== null) {
    return (
      <Alert severity="error">
        <AlertTitle>Submission failed</AlertTitle>
        {error}
      </Alert>
    );
  }

  if (outcome === null) {
    return null;
  }

  const { schemaWarning } = outcome;

  // Both flavors' ids belong under either outcome: the EDI transaction row and
  // the inbound API request row are written before the create runs, so a merge
  // is exactly when an operator needs them to inspect what the send recorded.
  const identifiers = (
    <>
      {typeof outcome.ediTransactionId === "string" && (
        <Typography variant="body2">
          EDI transaction id: {outcome.ediTransactionId}
        </Typography>
      )}
      {typeof outcome.inboundApiRequestUuid === "string" && (
        <Typography variant="body2">
          Inbound API request: {outcome.inboundApiRequestUuid}
        </Typography>
      )}
    </>
  );

  return (
    <Stack spacing={2}>
      {typeof schemaWarning === "string" && (
        <Alert severity="info">
          <AlertTitle>Payload does not match the inbound EDI schema</AlertTitle>
          <Typography variant="body2">
            It was forwarded raw, which is what the real endpoint does for
            partner payloads the schema rejects.
          </Typography>
          <Typography variant="body2">{schemaWarning}</Typography>
        </Alert>
      )}
      {outcome.merged ? (
        <Alert severity="warning">
          <AlertTitle>Merged into an existing order</AlertTitle>
          <Typography variant="body2">
            Surviving order: {outcome.mergedIntoOrderName ?? "unnamed"} (
            {outcome.mergedIntoOrderUuid})
          </Typography>
          {typeof outcome.message === "string" && (
            <Typography variant="body2">{outcome.message}</Typography>
          )}
          {identifiers}
        </Alert>
      ) : (
        <Alert severity="success">
          <AlertTitle>
            {typeof outcome.orderUuid === "string"
              ? "Order created"
              : "Processed with no order created"}
          </AlertTitle>
          {typeof outcome.orderUuid === "string" && (
            <Typography variant="body2">
              {outcome.orderName ?? "unnamed"} ({outcome.orderUuid})
            </Typography>
          )}
          {identifiers}
        </Alert>
      )}
    </Stack>
  );
};

export default OrderOutcomeAlert;
